import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetClientQuery } from "@/queries/useGetClientQuery";
import { useGetClubsQuery } from "@/queries/useGetClubsQuery";
import { useAuth } from "@/contexts/AuthContext";
import { getAppointmentServicesQuery, getAppointmentsQueryKey } from "@/utils/queryKeys";

interface Club {
  id: string;
  title: string;
}

interface ClubContextType {
  selectedClubId: string;
  selectedClubName: string;
  clubs: Club[];
  setSelectedClub: (clubId: string) => void;
}

const ClubContext = createContext<ClubContextType | undefined>(undefined);

const STORAGE_KEY = "selected_club_id";
const LEGACY_KEY = "id_club";

const getStoredClubId = () => localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_KEY);

const persistClubId = (clubId: string) => {
  localStorage.setItem(STORAGE_KEY, clubId);
  localStorage.setItem(LEGACY_KEY, clubId); // Sync legacy key for backwards compatibility
};

export const ClubProvider = ({ children }: { children: ReactNode }) => {
  const { isAuth } = useAuth();
  const queryClient = useQueryClient();

  // Don't seed from localStorage on mount — the stored id may reference a club
  // that's no longer in /clubs (legacy or different org), and would fire bad
  // requests before the effect below validates it. We let the effect resolve it
  // once clubsData is loaded; queries gated on `enabled: !!selectedClubId` wait.
  const [selectedClubId, setSelectedClubId] = useState("");

  const { data: clientData } = useGetClientQuery({ enabled: isAuth });
  const { data: clubsData = [] } = useGetClubsQuery({ enabled: isAuth });

  // Derive club name from clubs data
  const selectedClubName = clubsData.find((c) => c.id === selectedClubId)?.title || "";

  // Initialize/validate club selection when clubs data loads
  useEffect(() => {
    if (clubsData.length === 0) return;

    const storedClubId = getStoredClubId();
    const storedClubIsValid = storedClubId && clubsData.some((c) => c.id === storedClubId);

    if (storedClubIsValid) {
      // Use stored club if it's valid for current user
      setSelectedClubId(storedClubId);
    } else {
      // Fall back to user's default club (if valid) or first available
      const clientClubId = clientData?.club?.id;
      const clientClubIsValid = clientClubId && clubsData.some((c) => c.id === clientClubId);
      const defaultClubId = clientClubIsValid ? clientClubId : clubsData[0]?.id;
      if (defaultClubId) {
        setSelectedClubId(defaultClubId);
        persistClubId(defaultClubId);
      }
    }
  }, [clientData, clubsData]);

  const setSelectedClub = useCallback(
    (clubId: string) => {
      if (clubId === selectedClubId) return;

      setSelectedClubId(clubId);
      persistClubId(clubId);

      // Invalidate club-scoped queries when club changes
      queryClient.invalidateQueries({ queryKey: getAppointmentServicesQuery() });
      queryClient.invalidateQueries({ queryKey: getAppointmentsQueryKey() });
    },
    [selectedClubId, queryClient]
  );

  return (
    <ClubContext.Provider
      value={{
        selectedClubId,
        selectedClubName,
        clubs: clubsData,
        setSelectedClub,
      }}
    >
      {children}
    </ClubContext.Provider>
  );
};

export const useClub = () => {
  const context = useContext(ClubContext);
  if (context === undefined) {
    throw new Error("useClub must be used within a ClubProvider");
  }
  return context;
};
